import React from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import { ShieldAlert, Check, X, Loader2, AlertCircle, RefreshCw, MapPin, Clock } from "lucide-react";

/**
 * v23.8 — Admin: held jobs awaiting review.
 * Lists jobs auto-quarantined by the anti-fraud/risky-customer system. Admin
 * can Approve (releases back to drivers) or Reject (cancels).
 */
export default function AdminHeldJobsPanel() {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [busyId, setBusyId] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await apiClient.get("/admin/jobs/held");
      setData(r.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load held jobs");
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => { load(); }, [load]);

  const act = async (jobId, action) => {
    const verb = action === "approve" ? "Approve this job (drivers will see it)" : "Reject this job (cancel it, customer notified)";
    if (!window.confirm(verb + "?")) return;
    setBusyId(jobId);
    try {
      await apiClient.post(`/admin/jobs/${jobId}/${action}`);
      await load();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || `${action} failed`);
    } finally {
      setBusyId(null);
    }
  };

  const fmtMoney = (p) => p ? `£${(p / 100).toFixed(2)}` : "TBD";
  const fmtAgo = (iso) => {
    if (!iso) return "—";
    try {
      const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
      if (mins < 1) return "just now";
      if (mins < 60) return `${mins}m ago`;
      const h = Math.floor(mins / 60);
      if (h < 24) return `${h}h ago`;
      return `${Math.floor(h / 24)}d ago`;
    } catch { return iso; }
  };

  return (
    <section className="mb-12" data-testid="admin-held-jobs-section">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-lg font-bold tracking-tight text-[#0A0A0A] flex items-center gap-2">
            <ShieldAlert size={16} className="text-[#B91C1C]" /> Held jobs · awaiting review
          </h3>
          <p className="text-xs text-[#475569] max-w-2xl">
            Jobs auto-held because the customer has prior driver reports for ghosting, unpaid overtime, or abuse. Drivers <strong>cannot see</strong> these jobs until you Approve. Approve to release · Reject to cancel.
          </p>
        </div>
        <button onClick={load} data-testid="held-jobs-refresh" className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1E4D55] hover:text-[#0A0A0A]">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""}/> Refresh
        </button>
      </div>

      {error && (
        <div className="mb-3 flex items-start gap-2 text-xs text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] p-3" data-testid="held-jobs-error">
          <AlertCircle size={12} className="mt-0.5"/> {error}
        </div>
      )}

      {loading && !data && (
        <div className="text-xs text-[#475569] inline-flex items-center gap-2"><Loader2 size={12} className="animate-spin"/> Loading&hellip;</div>
      )}

      {data && data.count === 0 && (
        <div className="bg-[#5BB247]/10 border border-[#5BB247]/30 p-4 text-sm text-[#1f5d2e]" data-testid="held-jobs-empty">
          ✓ No held jobs — every recent posting is from a clean customer.
        </div>
      )}

      {data && data.count > 0 && (
        <div className="space-y-3" data-testid="held-jobs-list">
          {data.held_jobs.map((j) => (
            <div key={j.id} data-testid={`held-job-${j.id}`} className="bg-white border border-[#B91C1C]/30 border-l-4 border-l-[#B91C1C]">
              <div className="p-4 flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-[280px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#B91C1C] bg-[#FEE2E2] px-2 py-0.5">
                      {j.prior_report_count} prior report{j.prior_report_count === 1 ? "" : "s"}
                    </span>
                    <span className="text-[11px] text-[#475569] inline-flex items-center gap-1"><Clock size={11}/> held {fmtAgo(j.held_at)}</span>
                  </div>
                  <p className="font-display text-base font-bold mt-2 text-[#0A0A0A]">{j.title || "(no title)"}</p>
                  <p className="text-xs text-[#475569] mt-1">
                    <strong>{j.customer_email}</strong>{j.customer_name && ` · ${j.customer_name}`}
                  </p>
                  <p className="text-xs text-[#475569] mt-2 inline-flex items-start gap-1">
                    <MapPin size={11} className="mt-0.5 shrink-0"/>
                    <span>{j.pickup_address || "?"} <span className="text-[#0A0A0A]/40">→</span> {j.dropoff_address || "(none)"}</span>
                  </p>
                  <p className="text-xs text-[#475569] mt-2">
                    Category: <span className="font-mono text-[#0A0A0A]">{j.category}</span> · Amount: <span className="font-mono font-bold text-[#0A0A0A]">{fmtMoney(j.amount_pence)}</span> · Would restore to: <code className="text-[#1E4D55]">{j.pre_hold_status || "open"}</code>
                  </p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => act(j.id, "approve")}
                    disabled={busyId === j.id}
                    data-testid={`held-approve-${j.id}`}
                    className="inline-flex items-center gap-1.5 bg-[#5BB247] hover:bg-[#4A9939] text-[#0A0A0A] px-4 py-2 text-xs font-bold uppercase tracking-wider disabled:opacity-60"
                  >
                    {busyId === j.id ? <Loader2 size={12} className="animate-spin"/> : <Check size={12}/>} Approve
                  </button>
                  <button
                    onClick={() => act(j.id, "reject")}
                    disabled={busyId === j.id}
                    data-testid={`held-reject-${j.id}`}
                    className="inline-flex items-center gap-1.5 bg-white border border-[#B91C1C] text-[#B91C1C] hover:bg-[#B91C1C] hover:text-white transition-colors px-4 py-2 text-xs font-bold uppercase tracking-wider disabled:opacity-60"
                  >
                    <X size={12}/> Reject
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
